import * as React from "react"
import { cn } from "../lib/utils"

interface PageHeaderProps {
  title: React.ReactNode
  description?: React.ReactNode
  /** Optional icon component or node displayed in the primary badge box. */
  icon?: React.ElementType | React.ReactNode
  /** Right-aligned actions (buttons, menus). */
  actions?: React.ReactNode
  className?: string
  children?: React.ReactNode
}

/**
 * Enterprise Card Page Header (v4 Slate & Blue design system).
 * Renders as a white card surface with 1px border, rounded corners,
 * optional primary icon box, Poppins title, muted metadata, and right-aligned actions.
 */
export function PageHeader({
  title,
  description,
  icon: Icon,
  actions,
  className,
  children,
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        "p-4 rounded-xl border border-border bg-card shadow-xs flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0",
        className
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        {Icon && (
          <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-bold shrink-0">
            {React.isValidElement(Icon) ? (
              Icon
            ) : (
              (() => {
                const IconComponent = Icon as React.ElementType;
                return <IconComponent className="size-4.5" />;
              })()
            )}
          </div>
        )}
        <div className="min-w-0 space-y-0.5">
          <h1 className="truncate text-foreground font-display text-page-title tracking-tight font-semibold">
            {title}
          </h1>
          {description && (
            <p className="text-xs text-muted-foreground leading-relaxed truncate max-w-4xl">{description}</p>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {actions}
        </div>
      )}
      {children}
    </div>
  )
}
